import React, { useState, useEffect } from "react";
import { API, graphqlOperation } from "aws-amplify";

import { countInboundShipments } from "../../../graphql/inbound/custom-queries";
import { listAccountNames } from "../../../graphql/report-queries";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { startOfMonth, sub } from "date-fns";

import { useNotify, TopToolbar } from "react-admin";

import { ForceTitle } from "../../../components/common/ForceTitle";
import { BackButton } from "../../../components/common/BackButton";
import { logError } from "../../../utils/appUtils";
import { InventoryStatus } from "../../../utils/constants";
import Title from "../Title";

// /reportCarsByAccount
const AvgInboundCarsByAccount = () => {
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [average, setAverage] = useState(0);
  const [height, setHeight] = useState();

  useEffect(() => {
    getInboundsData();
  }, []);

  const getInboundsData = async () => {
    // Get the last 12 months, including current month
    const startDate = startOfMonth(sub(new Date(), { months: 11 }));
    let tempData = [];

    try {
      const queryList = [];

      let response = await API.graphql(
        graphqlOperation(listAccountNames, {
          reloadYardId: window.appUser?.reloadYardId,
          activeBusinessName: { beginsWith: { active: "true" } },
        })
      );
      const accounts = response.data.accountByActiveByName.items;
      setHeight(accounts.length * 50);

      accounts.forEach((account, idx) => {
        tempData[idx] = { name: account.businessName, cars: 0 };

        queryList[idx] = API.graphql(
          graphqlOperation(countInboundShipments, {
            filter: {
              reloadYardId: { eq: window.appUser?.reloadYardId },
              accountId: { eq: account.id },
              status: { ne: InventoryStatus.IN_TRANSIT },
              notifyDateTime: { gte: startDate },
            },
          })
        );
      });

      const promiseResults = await Promise.all(queryList);
      let totalAmt = 0;

      promiseResults.forEach((result, idx) => {
        if (result.data.searchInboundShipments.total) {
          const amt = result.data.searchInboundShipments.total;
          tempData[idx].cars = amt;
          totalAmt += amt;
        } else tempData[idx].cars = 0;
      });

      const avg = Math.round(totalAmt / tempData.length);
      setAverage(avg);
      setData(tempData);
    } catch (err) {
      logError("AvgInboundCarsByAccount:getInboundsData", err, notify);
    }
  };

  return (
    <>
      <div style={{ display: "none" }}>
        <ForceTitle title="Reports" />
      </div>
      <TopToolbar>
        <Title>Avg Inbound Cars by Account</Title>
        <BackButton basePath="/reports" />
      </TopToolbar>
      <ResponsiveContainer height={height}>
        <BarChart
          data={data}
          layout="vertical"
          barCategoryGap={6}
          margin={{ top: 5, right: 30, bottom: 5, left: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            type="category"
            dataKey="name"
            width={256}
            padding={{ left: 20 }}
          />
          <Tooltip />
          <Bar dataKey="cars" stackId="car">
            {data.map((entry, index) => (
              <Cell
                key={`bar-${index}`}
                fill={entry.cars > average ? "orange" : "blue"}
              />
            ))}
          </Bar>
          {average && (
            <ReferenceLine
              x={average}
              stroke="red"
              strokeWidth={5}
              strokeDasharray="6 6"
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default AvgInboundCarsByAccount;
